import { createFileRoute, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { DisabledCampaignState } from '@/campaigns/DisabledCampaignState';
import { PersonalCampaignCard } from '@/campaigns/PersonalCampaignCard';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { CampaignStudentBoardTable } from '@/tables/CampaignStudentBoardTable';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/campaigns',
)({
  component: CampaignsScreen,
});

function CampaignsScreen() {
  const { t } = useTranslation();
  const { groupName } = useParams({
    from: '/_app/_dashboard/groups/$groupName/campaigns',
  });

  const { data: group } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = group?.id;

  const { data: campaigns } = api.listGroupCampaigns.useQuery(
    { path: { group_id: groupId ?? 0 } },
    { enabled: !!groupId },
  );

  // A personal group holds at most one campaign — the study helper's own. It is
  // created on first enable, so "no campaign yet" and "campaign turned off"
  // both land on the disabled state.
  const campaign = campaigns?.[0];

  const { mutateAsync: enablePersonalCampaign } =
    api.enablePersonalCampaign.useMutation();

  const handleEnableCampaign = async (): Promise<void> => {
    if (!groupId) return;
    await enablePersonalCampaign({ path: { group_id: groupId } });
    await api.listGroupCampaigns.invalidateQueries();
  };

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion title={t('wbs.personal-campaign')} />
      </GroupHeaderWrapper>
      {campaign?.is_active ? (
        <>
          <PersonalCampaignCard campaign={campaign} />
          <CampaignStudentBoardTable campaignId={campaign.id} />
        </>
      ) : (
        <DisabledCampaignState onEnable={handleEnableCampaign} />
      )}
    </>
  );
}
